import React from 'react'
import useMessages from '../../../../hooks/useMessages'

// types
import { ConfigResponse, SalonDetail } from '../../../../types/types'

// components
import OpeningHours from '../OpeningHours/OpeningHours'
import PaymentDetails from '../PaymentDetails/PaymentDetails'
import VisuallyHidden from '../../../../atoms/VisuallyHidden/VisuallyHidden'

// assets
import MapPinIcon from '../../../../assets/icons/MapPinIcon'
import PhoneIcon from '../../../../assets/icons/PhoneIcon'
import ParkingIcon from '../../../../assets/icons/ParkingIcon'
import CreditCardIcon from '../../../../assets/icons/CreditCardIcon'
import MapIcon from '../../../../assets/icons/MapIcon'
// import LoyaltyIcon from '../../../../assets/icons/LoyaltyIcon'

// utils
import { getGoogleMapsLink, getPhoneNumber } from '../../../../utils/helper'
import { SALON_TYPE } from '../../../../utils/enums'
import { getViewOnMapEvent } from '../../../../utils/dataLayerEvents'
import { pushToDataLayer } from '../../../../utils/dataLayer'

// styles
import * as SC from './SalonContactsStyles'

export const SALON_CONTACTS_HEADING_ID = 'salon-contacts-heading'

type Props = {
	salonData: SalonDetail
	// loyaltyConfigData: LoyaltyConfigResponse
	countriesData: ConfigResponse['rolloutCountries']
}

const SalonContacts = (props: Props) => {
	const { salonData, countriesData /* loyaltyConfigData */ } = props

	const isBasic = salonData.createType === SALON_TYPE.BASIC

	const { messages } = useMessages()

	// google map link
	const googleMapLink = getGoogleMapsLink({ lat: salonData.address?.latitude, lon: salonData.address?.longitude })

	const sendMapClickEvent = () => {
		const event = getViewOnMapEvent({ interaction: 'text_detail' })
		pushToDataLayer(event)
	}

	return (
		<SC.SalonContactsContainer>
			{/* One section heading for the contacts sidebar; subsections use h3. */}
			<VisuallyHidden as={'h2'} id={SALON_CONTACTS_HEADING_ID}>
				{messages['Contact us']}
			</VisuallyHidden>

			{/* opening hours */}
			{!isBasic && salonData.openingHoursData && (
				<SC.InfoContainer>
					{/* TODO: find out nicer way to set margin on status */}
					<OpeningHours openingHours={salonData.openingHoursData} openingStatusMarginLeft={34} titleAs={'h3'} />
				</SC.InfoContainer>
			)}

			{/* address */}
			{salonData.formattedAddress && (
				<SC.InfoContainer>
					<SC.TitleContainer>
						<MapPinIcon aria-hidden />
						<SC.Title>{messages['Where to find us']}</SC.Title>
					</SC.TitleContainer>
					<SC.AddressContainer>
						<SC.DataContainer>
							<span>{salonData.formattedAddress}</span>
							{salonData.address?.description && <SC.AddressNote>{salonData.address.description}</SC.AddressNote>}
							{!isBasic ? (
								<>
									<SC.MapLinkDesktop href='#google-map' onClick={sendMapClickEvent}>
										{messages['Show on map']}
									</SC.MapLinkDesktop>
									<SC.LinkButtonMobile href='#google-map' onClick={sendMapClickEvent}>
										<MapIcon aria-hidden />
										<span>{messages.Map}</span>
									</SC.LinkButtonMobile>
								</>
							) : (
								googleMapLink && (
									<SC.BasicSalonLinkWrapper>
										<SC.BasicSalonLink
											href={googleMapLink}
											target='_blank'
											rel={'nofollow noopener noreferrer'}
											onClick={sendMapClickEvent}
										>
											{messages['Show on map']}
										</SC.BasicSalonLink>
									</SC.BasicSalonLinkWrapper>
								)
							)}
						</SC.DataContainer>
					</SC.AddressContainer>
				</SC.InfoContainer>
			)}

			{/* contact us - phone and email */}
			{(salonData.email || salonData.phones) && (
				<SC.InfoContainer>
					<SC.TitleContainer>
						<PhoneIcon aria-hidden />
						<SC.Title>{messages['Contact us']}</SC.Title>
					</SC.TitleContainer>
					<SC.DataContainer>
						{salonData.email && <SC.ContactLink href={`mailto:${salonData.email}`}>{salonData.email}</SC.ContactLink>}
						{salonData.phones &&
							salonData.phones.length > 0 &&
							salonData.phones.map((phone) => {
								const phoneNumber = getPhoneNumber(phone, countriesData)
								return (
									<SC.ContactLink key={phone.id} href={`tel:${phoneNumber}`}>
										{phoneNumber}
									</SC.ContactLink>
								)
							})}
					</SC.DataContainer>
				</SC.InfoContainer>
			)}

			{/* parking */}
			{!isBasic && salonData.parkingNote && (
				<SC.InfoContainer>
					<SC.TitleContainer>
						<ParkingIcon aria-hidden />
						<SC.Title>{messages.Parking}</SC.Title>
					</SC.TitleContainer>
					<SC.DataContainer>{salonData.parkingNote}</SC.DataContainer>
				</SC.InfoContainer>
			)}

			{/* payment */}
			{!isBasic && (salonData.payByCard || salonData.payByCash || salonData.otherPaymentMethods) && (
				<SC.InfoContainer>
					<SC.TitleContainer>
						<CreditCardIcon aria-hidden />
						<SC.Title>{messages['Payment information']}</SC.Title>
					</SC.TitleContainer>
					<SC.DataContainer>
						<PaymentDetails salonData={salonData} />
					</SC.DataContainer>
				</SC.InfoContainer>
			)}
			{/* // NOTE: Loyalty program is currently hidden */}
			{/* {loyaltyConfigData.loyaltySalonDetail.loyalty.showLoyalty && ( */}
			{/*	<SC.InfoContainer> */}
			{/*		<SC.TitleContainer> */}
			{/*			<LoyaltyIcon /> */}
			{/*			<SC.Title>{messages['Loyalty program']}</SC.Title> */}
			{/*		</SC.TitleContainer> */}
			{/*		<SC.DataContainer> */}
			{/*			<SC.LoyaltyText dangerouslySetInnerHTML={{ __html: loyaltyConfigData.loyaltySalonDetail.loyalty.text }} /> */}
			{/*		</SC.DataContainer> */}
			{/*	</SC.InfoContainer> */}
			{/* )} */}
		</SC.SalonContactsContainer>
	)
}

export default SalonContacts
